import type { Toolbox } from "@reactivated";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

/**
 * First-block types that make the header render transparently over the page.
 * `satisfies` validates each string against real Toolbox block types without
 * widening, so a typo here is a compile error and the literals stay a single
 * source of truth for both the type and the runtime guard below.
 */
const overlapBlockTypes = [
    "hero_carousel",
    "full_width_hero",
] as const satisfies readonly Toolbox[number]["type"][];

type OverlapType = (typeof overlapBlockTypes)[number];
type OverlapBlock = Extract<Toolbox[number], { type: OverlapType }>;

/**
 * Narrows a page body block to one of the overlap types. Generic because page
 * bodies are supersets of Toolbox with differing unions; the narrowing only
 * resolves to a concrete block when called against a template's body.
 */
export const isOverlapBlockType = <T extends { type: string }>(
    block: T,
): block is Extract<T, { type: OverlapType }> =>
    overlapBlockTypes.some((type) => type === block.type);

/** Whether the header renders transparently over the given first body block */
export const firstBlockOverlapsNav = (
    block: OverlapBlock | undefined,
): boolean => {
    if (!block) return false;
    switch (block.type) {
        case "hero_carousel":
            return true;
        case "full_width_hero": {
            const { height } = block.value;
            switch (height) {
                case "viewport":
                case null:
                    return true;
                case "large":
                case "small":
                    return false;
            }
            return assertNever(height);
        }
    }
    return assertNever(block);
};
